/**
 * Geographic seeding algorithm for tournament groups.
 * Distributes countries from different subregions across groups for variety.
 */

import { getCountryByCode } from '@/data/countries';
import type { CountryCode, Subregion, Continent } from '@/data/countries/types';
import type { TournamentGroup, AdvancingCountry } from './types';

/**
 * Get the geographic key for a country (subregion or continent).
 */
function getGeoKey(code: CountryCode): string {
  const country = getCountryByCode(code);
  if (!country) return 'unknown';
  return country.subregion ?? country.continent;
}

/**
 * Shuffle an array using Fisher-Yates algorithm.
 */
function shuffle<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Calculate optimal group sizes for a given number of countries.
 *
 * Groups are sized by the provided countriesPerGroup parameter.
 * The number of groups is determined by: ceil(countryCount / countriesPerGroup)
 *
 * For ≤16 countries: Go directly to bracket (handled elsewhere).
 *
 * @param countryCount - Total number of countries
 * @param countriesPerGroup - Target countries per group (default: 6 for desktop, 3 for mobile)
 */
export function calculateGroupSizes(countryCount: number, countriesPerGroup: number = 6): number[] {
  // For 16 or fewer, we skip groups entirely and go to bracket
  if (countryCount <= 16) return [countryCount];

  // Calculate number of groups based on desired group size
  const numGroups = Math.ceil(countryCount / countriesPerGroup);

  // Distribute countries as evenly as possible across groups
  const baseSize = Math.floor(countryCount / numGroups);
  const remainder = countryCount % numGroups;

  const sizes: number[] = [];
  for (let i = 0; i < numGroups; i++) {
    // First 'remainder' groups get one extra country
    sizes.push(baseSize + (i < remainder ? 1 : 0));
  }

  return sizes;
}

/**
 * Seed countries into groups with geographic diversity.
 * Uses a serpentine draft pattern to distribute countries from
 * different subregions across groups.
 *
 * @param countryCodes - Array of country codes to seed
 * @param countriesPerGroup - Target countries per group (default: 6 for desktop, 3 for mobile)
 */
export function seedGroups(countryCodes: CountryCode[], countriesPerGroup: number = 6): TournamentGroup[] {
  if (countryCodes.length === 0) return [];

  // Group countries by their geographic region
  const byRegion = new Map<string, CountryCode[]>();
  for (const code of countryCodes) {
    const key = getGeoKey(code);
    if (!byRegion.has(key)) {
      byRegion.set(key, []);
    }
    byRegion.get(key)!.push(code);
  }

  // Shuffle within each region for variety
  for (const [key, codes] of byRegion) {
    byRegion.set(key, shuffle(codes));
  }

  // Calculate group sizes
  const groupSizes = calculateGroupSizes(countryCodes.length, countriesPerGroup);
  const numGroups = groupSizes.length;

  // Initialize empty groups
  const groups: CountryCode[][] = Array.from({ length: numGroups }, () => []);

  // Create a pool of countries sorted by region size (larger regions first)
  // This helps distribute larger regions more evenly
  const regions = Array.from(byRegion.entries())
    .sort((a, b) => b[1].length - a[1].length);

  // Serpentine draft: fill groups using alternating direction
  let groupIndex = 0;
  let direction = 1; // 1 = forward, -1 = backward

  for (const [, regionCountries] of regions) {
    for (const code of regionCountries) {
      // Find a group that still has room
      let attempts = 0;
      while (groups[groupIndex].length >= groupSizes[groupIndex] && attempts < numGroups) {
        groupIndex = (groupIndex + direction + numGroups) % numGroups;
        attempts++;
      }

      groups[groupIndex].push(code);

      // Move to next group in serpentine pattern
      groupIndex += direction;
      if (groupIndex >= numGroups) {
        groupIndex = numGroups - 1;
        direction = -1;
      } else if (groupIndex < 0) {
        groupIndex = 0;
        direction = 1;
      }
    }
  }

  // Shuffle order within each group for presentation variety
  const shuffledGroups = groups.map(g => shuffle(g));

  // Convert to TournamentGroup objects
  return shuffledGroups.map((countries, index) => ({
    id: `group-${index + 1}`,
    countries,
    ratings: null,
    rankings: null,
  }));
}

/**
 * Reseed advancing countries for the next round.
 * Tries to avoid putting countries from the same previous group together.
 *
 * @param advancingCountries - Countries advancing to next round
 * @param previousGroups - Groups from the previous round
 * @param countriesPerGroup - Target countries per group (default: 6 for desktop, 3 for mobile)
 */
export function reseedForNextRound(
  advancingCountries: AdvancingCountry[],
  previousGroups: TournamentGroup[],
  countriesPerGroup: number = 6
): TournamentGroup[] {
  // Create a map of country -> previous group ID
  const previousGroupMap = new Map<CountryCode, string>();
  for (const group of previousGroups) {
    for (const code of group.countries) {
      previousGroupMap.set(code, group.id);
    }
  }

  // Sort by points (higher points = better seed)
  const sorted = [...advancingCountries].sort((a, b) => b.points - a.points);
  const codes = sorted.map(c => c.code);

  // Use geographic seeding, but also try to separate previous groupmates
  const groups = seedGroups(codes, countriesPerGroup);

  // Post-process: if two countries from the same previous group are together,
  // try to swap one with another group
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    const prevGroupIds = group.countries.map(c => previousGroupMap.get(c));

    // Check for duplicates
    const seen = new Set<string>();
    for (let j = 0; j < group.countries.length; j++) {
      const prevId = prevGroupIds[j];
      if (prevId && seen.has(prevId)) {
        // Try to swap with another group
        for (let k = 0; k < groups.length; k++) {
          if (k === i) continue;

          for (let l = 0; l < groups[k].countries.length; l++) {
            const otherPrevId = previousGroupMap.get(groups[k].countries[l]);
            // Can swap if other country's prev group isn't in current group
            if (!prevGroupIds.includes(otherPrevId)) {
              // Swap
              const temp = group.countries[j];
              group.countries[j] = groups[k].countries[l];
              groups[k].countries[l] = temp;
              break;
            }
          }
        }
      }
      if (prevId) seen.add(prevId);
    }
  }

  return groups;
}

/**
 * Get the number of countries that advance from groups.
 * Top 2 from each group advance.
 */
export function getAdvancingCount(groupCount: number): number {
  return groupCount * 2;
}
